/**
 * Glia Inject
 */

const _SCRIPT_ID = 'glia_enabled';
const _SCRIPT_SRC = 'https://api.glia.com/salemove_integration.js';
const _SCRIPT_DELAY = 0;

/** 
 * Allows Glia to view iframe content by adding the Glia script to all iframes.
 */
export default {
    
    _tag: 'GliaInject',

    _mutation: null,

    // Spanish locale handling
    _spanishLocale: {
        bindEvents() {
            // Initial check.
            this.checkAndSetSpanishLocale();
            
            // Listen to browser back/forward.
            window.addEventListener('popstate', this.checkAndSetSpanishLocale.bind(this));
            
            // Listen to regular navigation.
            const pushState = history.pushState;
            history.pushState = (...args) => {
                pushState.apply(history, args);
                this.checkAndSetSpanishLocale();
            };
        },
        
        unbindEvents() {
            window.removeEventListener('popstate', this.checkAndSetSpanishLocale);
        },
        
        checkAndSetSpanishLocale() {
            if (window.location.pathname === '/spanish-chat/') {  // This condition can be modified for different pages
                try {
                    if (typeof sm === 'undefined') {
                        console.log('Glia not available in this environment');
                        return;
                    }
                    
                    sm.getApi({version: 'v1'}).then(function(api){
                        api.setLocale('Spanish');
                    }).catch(function(err) {
                        console.log('Failed to set Glia locale:', err);
                    });
                    console.log("l10n file loaded");
                } catch (err) {
                    console.log('Glia locale setup failed:', err);
                }
            }
        }
    },

    /**
     * Runs the Glia inject script once, and then on observed DOM changes.
     * 
     * @param {Element | undefined} container
     */
    run(container) {
        
        this._stop();
        
        // Determine container.
        container = container || document;
        this._injectIntoIframes(container); // inject into all iframes at start
        this._spanishLocale.bindEvents();
        
        // Mutation observer will allow scripts to run in response to DOM changes.
        this._mutation = new MutationObserver(this._mutation_next.bind(this));
        this._mutation.observe(container, {
            attributes: false,
            characterData: false,
            childList: true,
            subtree: false // less aggressive, only direct children
        });
    },

    /**
     * Recursively injects Glia script into all iframes within the container.
     * @param {Element|Document} container
     */
    _injectIntoIframes(container) {
        const iframes = container.getElementsByTagName('iframe');
        for (const iframe of iframes) {
            this._iframe_run(iframe);
            // Try to recursively inject into nested iframes (if same-origin)
            try {
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (doc) this._injectIntoIframes(doc);
            } catch (e) { /* cross-origin, ignore */ }
        }
    },

    /**
     * Runs the Glia inject script once.
     * 
     * @param {Element | undefined} container
     */
    runOnce(container) {
        container = container || document;
        this._injectIntoIframes(container);
    },

    /**
     * Stops the Glia inject script.
     */
    stop() {

        this._stop();
    },

    _stop() {

        // Stop document observer(s).
        this._mutation?.disconnect();
        this._mutation = null;
        
        // Clean up Spanish locale listeners.
        this._spanishLocale.unbindEvents();
    },

    /**
     * 
     * @param {MutationRecord[]} records
     */
    _mutation_next(records) {
        for (const record of records) {
            for (const node of record.addedNodes) {
                if (node.nodeName === 'IFRAME') {
                    this._iframe_run(node);
                    // Recursively inject into nested iframes
                    try {
                        const doc = node.contentDocument || node.contentWindow?.document;
                        if (doc) this._injectIntoIframes(doc);
                    } catch (e) {}
                }
                // If a div or other element is added, check for iframes inside it
                if (node.nodeType === 1) { // ELEMENT_NODE
                    this._injectIntoIframes(node);
                }
            }
        }
    },

    /**
     * Gets the document element for the specified iframe.
     * @param {HTMLIFrameElement} iframe
     * @returns {Document | null} The iframe's document element if able/available, null otherwise.
     */
    _iframe_getDocument(iframe) {
        try {
            return iframe.contentDocument || iframe.contentWindow?.document || null;
        }
        catch (ex) {
            this._warn(ex);
        }
        return null;
    },

    /**
     * @param {HTMLDivElement} div
     * @returns
     */
    _div_run(div) {
        this._injectIntoIframes(div);
    },

    /**
     * @param {HTMLIFrameElement} iframe
     * @returns
     */
    _iframe_run(iframe) {

        // Validate input.
        if (!this._visible(iframe)) {
            return;
        }

        // Get iframe document.
        let iframeDocument = this._iframe_getDocument(iframe);
        if (iframeDocument == null) {
            return;
        }

        switch (iframeDocument.readyState) {
            case 'interactive':
            case 'complete':
                // If iframe document is ready, proceed to inject.
                setTimeout(() => this._iframe_document_inject(iframeDocument), _SCRIPT_DELAY);
                break;
            default:
                // Otherwise, listen for iframe document ready state change.
                iframeDocument.addEventListener('readystatechange', this._iframe_document_readyStateChanged.bind(this));
                return;
        }
    },

    /**
     * Determines whether the element is visible or potentially visible to the user.
     * @param {Element} el
     * @returns {boolean} 
     */
    _visible(el) {
        return el && el.offsetWidth > 0 && el.offsetHeight > 0;
    },

    _iframe_document_readyStateChanged(e) {
        let iframeDocument = e.currentTarget || e.target;
        switch (iframeDocument.readyState) {
            case 'interactive':
            case 'complete':
                // If iframe document is ready, proceed to inject.
                setTimeout(() => this._iframe_document_inject(iframeDocument), _SCRIPT_DELAY);
                break;
            default:
                break;
        }
    },

    /**
     * Injects the Glia script into the Document of the supplied HTMLIFrameElement reference.
     * 
    * @param {HTMLIFrameElement} iframe
    * @returns {boolean} True if successful.
    */
    _iframe_inject(iframe) {

        // Validate input.
        if (iframe == null) {
            return false;
        }

        // Get iframe document.
        let iframeDocument = this._iframe_getDocument(iframe);

        // Inject script into iframe document.
        return this._iframe_document_inject(iframeDocument);
    },

    /**
     * Injects the Glia script into the supplied Document reference.
     * 
     * @param {Document} iframeDocument
     * @returns {boolean} True if successful.
     */
    _iframe_document_inject(iframeDocument) {

        // Validate input.
        if (iframeDocument == null) {
            return false;
        }
        
        // Check for existing script.
        if (iframeDocument.getElementById(_SCRIPT_ID)) {
            return false;
        }
        
        try {

            // Create script.
            let script = iframeDocument.createElement('script');
            script.setAttribute('id', _SCRIPT_ID);
            script.setAttribute('src', _SCRIPT_SRC);
            script.setAttribute('async', '');

            // Inject script.
            return !!iframeDocument.head.appendChild(script);
        }
        catch (ex) {

            this._error(ex);
        }
        
        return false;
    },

    _log(message) {
        console.log(`${this._tag}: ${message}`);
    },
    _warn(message) {
        console.warn(`${this._tag}: ${message}`);
    },
    _error(message) {
        console.error(`${this._tag}: ${message}`);
    }
}